import { aiNodeContent, aiTreeSlides } from './langchainSceneDemo';

const baseLearner = {
  name: 'Guest Learner',
  educationLevel: 'General',
  interests: 'technology, global affairs, science, education',
  careerGoals: 'future-ready skills',
};

const baseAnimation = {
  mode: 'chalkboard',
  requireLevelOrder: true,
  includePrerequisiteEdges: true,
};

const baseScenePolicy = {
  requireScene: true,
  requireTimeline: true,
  requireAnimationPlan: true,
};

const makeTreeSlides = (prefix, title, childTitles) => [
  {
    id: `${prefix}_intro`,
    title,
    type: 'text',
    physics: { enabled: false, mode: 'none', params: {} },
    timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
    children: childTitles.map((childTitle, index) => ({
      id: `${prefix}_node_${index + 1}`,
      title: childTitle,
      type: 'diagram',
      physics: { enabled: true, mode: 'spring_expand', params: { damping: 0.85 } },
      timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
      children: [],
    })),
  },
];

const buildMiddleEastStaticScene = () => ({
  lessonId: 'static_middle_east_history',
  title: 'History of the Middle East',
  treeSlides: makeTreeSlides('middle_east', 'Historical Framing', [
    'Empires and Border Formation',
    'Modern State Development',
  ]),
  nodeContentById: {
    middle_east_intro: {
      topicId: 'middle_east_intro',
      explanation:
        'The modern Middle East is best understood as a sequence of imperial decline, border redesign, state formation, and recurring geopolitical conflict.',
      keyPoints: [
        'Historical continuity matters more than isolated events',
        'Modern borders emerged from political decisions, not natural lines',
        'State formation and conflict remained tightly linked',
      ],
      examples: ['Ottoman decline to mandate-era restructuring'],
      bridgeFromPrevious: '',
      bridgeToNext: 'Next we place the key transitions on a timeline.',
      pedagogicalDiagram: {
        variant: 'timeline',
        pattern: 'PROGRESSIVE_REVEAL',
        title: 'Major Historical Phases',
        subtitle: 'From imperial collapse to modern regional order',
        durationSec: 6,
        timeline: {
          events: [
            { id: 'e1', label: 'Late Ottoman era', atK: 0.12 },
            { id: 'e2', label: 'Mandate system', atK: 0.34 },
            { id: 'e3', label: 'State formation', atK: 0.62 },
            { id: 'e4', label: 'Contemporary conflicts', atK: 0.86 },
          ],
        },
      },
    },
    middle_east_node_1: {
      topicId: 'middle_east_node_1',
      explanation:
        'As empires weakened, European powers and local actors reshaped borders, institutions, and political expectations across the region.',
      keyPoints: [
        'External rule often redrew governance before local legitimacy settled',
        'Borders influenced future conflict and identity',
        'Institutional design outlasted the mandate period',
      ],
      examples: ['Mandate governance shaping later state structures'],
      bridgeFromPrevious: 'The timeline gives the sequence.',
      bridgeToNext: 'Then we compare how those shifts affected state development.',
      pedagogicalDiagram: {
        variant: 'graph',
        pattern: 'DEPENDENCY_PROPAGATION',
        title: 'Empire to Mandate Causality',
        subtitle: 'How transitions shaped later state conditions',
        durationSec: 6,
        graph: {
          nodes: [
            { id: 'a', label: 'Imperial decline', xk: 0.16, yk: 0.38 },
            { id: 'b', label: 'External administration', xk: 0.42, yk: 0.38 },
            { id: 'c', label: 'Border settlement', xk: 0.68, yk: 0.38 },
            { id: 'd', label: 'Long-term instability', xk: 0.82, yk: 0.68 },
          ],
          edges: [
            { from: 'a', to: 'b', label: 'power vacuum', directed: true },
            { from: 'b', to: 'c', label: 'governance choices', directed: true },
            { from: 'c', to: 'd', label: 'lasting pressure', directed: true },
          ],
        },
      },
    },
    middle_east_node_2: {
      topicId: 'middle_east_node_2',
      explanation:
        'Modern states developed under uneven conditions, balancing sovereignty, identity, institutions, and outside pressure.',
      keyPoints: [
        'State capacity developed unevenly across the region',
        'Political legitimacy and external influence often collided',
        'Historical pressures still shape present dynamics',
      ],
      examples: ['Different trajectories of centralized and fragmented states'],
      bridgeFromPrevious: 'State outcomes depended on the earlier transitions.',
      bridgeToNext: '',
      pedagogicalDiagram: {
        variant: 'table',
        pattern: 'PARALLEL_COMPARISON',
        title: 'State Formation Pressures',
        subtitle: 'Three recurring forces across modern states',
        durationSec: 6,
        table: {
          headers: ['Force', 'Effect', 'Long-term Result'],
          rows: [
            ['Borders', 'Compressed identities', 'Persistent tension'],
            ['Institutions', 'Uneven state capacity', 'Governance asymmetry'],
            ['External powers', 'Strategic dependence', 'Policy constraint'],
          ],
        },
      },
    },
  },
});

const buildEducationStaticScene = () => ({
  lessonId: 'static_education_revolution',
  title: 'Revolution of Education Systems',
  treeSlides: makeTreeSlides('education', 'Why Education Systems Change', [
    'Mass Schooling to Digital Learning',
    'Personalized AI-Native Education',
  ]),
  nodeContentById: {
    education_intro: {
      topicId: 'education_intro',
      explanation:
        'Education systems changed whenever society needed new forms of literacy, scale, coordination, and economic participation.',
      keyPoints: [
        'Education reflects social and technological needs',
        'Mass schooling prioritized scale and standardization',
        'Digital systems reopened the possibility of personalization',
      ],
      examples: ['Industrial-era classrooms versus adaptive digital platforms'],
      bridgeFromPrevious: '',
      bridgeToNext: 'Next we trace the major system shifts over time.',
      pedagogicalDiagram: {
        variant: 'timeline',
        pattern: 'PROGRESSIVE_REVEAL',
        title: 'Evolution of Education Systems',
        subtitle: 'Elite instruction to AI-personalized learning',
        durationSec: 6,
        timeline: {
          events: [
            { id: 'e1', label: 'Elite tutoring', atK: 0.12 },
            { id: 'e2', label: 'Mass schooling', atK: 0.38 },
            { id: 'e3', label: 'Digital platforms', atK: 0.66 },
            { id: 'e4', label: 'AI-guided learning', atK: 0.86 },
          ],
        },
      },
    },
    education_node_1: {
      topicId: 'education_node_1',
      explanation:
        'The shift from industrial schooling to digital delivery changed access and flexibility, but often kept one-size-fits-all structures intact.',
      keyPoints: [
        'Digital access expanded reach without fully changing pedagogy',
        'Content became abundant while guidance stayed scarce',
        'Learners gained flexibility but also more fragmentation',
      ],
      examples: ['Recorded lectures replacing but not redesigning classrooms'],
      bridgeFromPrevious: 'The historical timeline sets up the transition.',
      bridgeToNext: 'Then we compare fixed systems with adaptive learning.',
      pedagogicalDiagram: {
        variant: 'chart',
        pattern: 'STRUCTURAL_TRANSFORMATION',
        title: 'From Standardized to Flexible Delivery',
        subtitle: 'Scale improves before personalization catches up',
        durationSec: 6,
        chart: {
          type: 'line',
          labels: ['Elite', 'Mass', 'Digital', 'AI'],
          series: [
            { name: 'Access', values: [0.1, 0.65, 0.82, 0.9] },
            { name: 'Personalization', values: [0.7, 0.2, 0.35, 0.88] },
          ],
        },
      },
    },
    education_node_2: {
      topicId: 'education_node_2',
      explanation:
        'AI-native learning systems can adapt pace, explanation style, and sequence, making education more responsive to the individual learner.',
      keyPoints: [
        'Adaptive sequencing is the main structural shift',
        'Agents can coordinate content, feedback, and pacing',
        'Personalization works best when it remains goal-aligned',
      ],
      examples: ['Agent-guided learning paths based on learner behavior'],
      bridgeFromPrevious: 'Digital delivery created the infrastructure.',
      bridgeToNext: '',
      pedagogicalDiagram: {
        variant: 'flow_diagram',
        pattern: 'DEPENDENCY_PROPAGATION',
        title: 'AI-Personalized Learning Loop',
        subtitle: 'Profile, diagnose, adapt, coach, reflect',
        durationSec: 6,
        flowDiagram: {
          stages: [
            { id: 's1', label: 'Learner profile', xk: 0.12, yk: 0.5 },
            { id: 's2', label: 'Diagnosis', xk: 0.32, yk: 0.5 },
            { id: 's3', label: 'Adaptive plan', xk: 0.52, yk: 0.5 },
            { id: 's4', label: 'Guided practice', xk: 0.72, yk: 0.5 },
            { id: 's5', label: 'Reflection', xk: 0.88, yk: 0.5 },
          ],
          links: [
            { from: 's1', to: 's2', label: 'context' },
            { from: 's2', to: 's3', label: 'needs' },
            { from: 's3', to: 's4', label: 'path' },
            { from: 's4', to: 's5', label: 'feedback' },
          ],
        },
      },
    },
  },
});

const buildDifferentialEquationsStaticScene = () => ({
  lessonId: 'static_differential_equations',
  title: 'Differential Equations',
  treeSlides: makeTreeSlides('diff_eq', 'Why Differential Equations Matter', [
    'Rate of Change as a Model',
    'Solution Behavior Over Time',
  ]),
  nodeContentById: {
    diff_eq_intro: {
      topicId: 'diff_eq_intro',
      explanation:
        'Differential equations model systems where change depends on the current state, making them central to physics, biology, economics, and engineering.',
      keyPoints: [
        'They connect present state to future behavior',
        'Many real systems are naturally expressed as rates of change',
        'Solutions reveal system behavior over time',
      ],
      examples: ['Population growth, cooling, motion, electrical circuits'],
      bridgeFromPrevious: '',
      bridgeToNext: 'Next we convert intuition about change into a formal model.',
      pedagogicalDiagram: {
        variant: 'coordinate_plane',
        pattern: 'STRUCTURAL_TRANSFORMATION',
        title: 'State and Change',
        subtitle: 'A system evolves because its rate depends on where it is',
        durationSec: 6,
        coordinatePlane: {
          points: [
            { id: 'p0', label: 'Initial state', xk: 0.14, yk: 0.78 },
            { id: 'p1', label: 'Later state', xk: 0.42, yk: 0.52 },
            { id: 'p2', label: 'Long-term state', xk: 0.74, yk: 0.32 },
          ],
          curves: [
            {
              id: 'c1',
              points: [
                { xk: 0.1, yk: 0.82 },
                { xk: 0.34, yk: 0.62 },
                { xk: 0.58, yk: 0.44 },
                { xk: 0.8, yk: 0.3 },
              ],
            },
          ],
        },
      },
    },
    diff_eq_node_1: {
      topicId: 'diff_eq_node_1',
      explanation:
        'A differential equation encodes how a quantity changes, so the equation itself is a compressed description of behavior.',
      keyPoints: [
        'The derivative expresses local change',
        'Parameters shape the trajectory',
        'Initial conditions select one concrete solution',
      ],
      examples: ['dy/dt = -ky captures exponential decay'],
      bridgeFromPrevious: 'The graph shows evolving state.',
      bridgeToNext: 'Then we inspect how solution shapes differ over time.',
      pedagogicalDiagram: {
        variant: 'table',
        pattern: 'STEP_EXECUTION',
        title: 'Equation Components',
        subtitle: 'What each term tells you about the system',
        durationSec: 6,
        table: {
          headers: ['Term', 'Meaning', 'Effect'],
          rows: [
            ['dy/dt', 'Current rate', 'How fast the system moves'],
            ['y', 'Current state', 'What the rate depends on'],
            ['k', 'Parameter', 'How strongly the system reacts'],
          ],
        },
      },
    },
    diff_eq_node_2: {
      topicId: 'diff_eq_node_2',
      explanation:
        'Different equations produce different long-term behaviors, such as growth, decay, oscillation, or stabilization.',
      keyPoints: [
        'Solution shape reflects system assumptions',
        'Long-term behavior matters as much as local change',
        'Visualization reveals stability and trend',
      ],
      examples: ['Exponential growth versus exponential decay'],
      bridgeFromPrevious: 'Equation structure predicts trajectory shape.',
      bridgeToNext: '',
      pedagogicalDiagram: {
        variant: 'chart',
        pattern: 'PARALLEL_COMPARISON',
        title: 'Solution Behavior Comparison',
        subtitle: 'Growth and decay diverge rapidly',
        durationSec: 6,
        chart: {
          type: 'line',
          labels: ['t0', 't1', 't2', 't3', 't4'],
          series: [
            { name: 'Growth', values: [1, 1.5, 2.3, 3.4, 5.2] },
            { name: 'Decay', values: [1, 0.68, 0.46, 0.31, 0.21] },
          ],
        },
      },
    },
  },
});

const buildClimateStaticScene = () => ({
  lessonId: 'static_climate_systems',
  title: 'Climate Systems and Change',
  treeSlides: makeTreeSlides('climate', 'Climate as a Connected System', [
    'Feedback Loops and Forcing',
    'Human Impact on Long-Term Trends',
  ]),
  nodeContentById: {
    climate_intro: {
      topicId: 'climate_intro',
      explanation:
        'Climate is a connected system of atmosphere, oceans, land, ice, and biosphere, where small changes can propagate through feedback loops over long timescales.',
      keyPoints: [
        'Climate behavior emerges from linked subsystems',
        'Feedback loops amplify or dampen change',
        'Long-term trends matter more than daily weather noise',
      ],
      examples: ['Ocean heat storage influencing atmospheric temperature'],
      bridgeFromPrevious: '',
      bridgeToNext: 'Next we look at how forcing and feedback interact.',
      pedagogicalDiagram: {
        variant: 'flow_diagram',
        pattern: 'ENERGY_FLOW_SIMULATION',
        title: 'Climate System Coupling',
        subtitle: 'Energy and matter move across linked subsystems',
        durationSec: 6,
        flowDiagram: {
          stages: [
            { id: 's1', label: 'Sunlight', xk: 0.1, yk: 0.42 },
            { id: 's2', label: 'Atmosphere', xk: 0.3, yk: 0.42 },
            { id: 's3', label: 'Ocean', xk: 0.52, yk: 0.62 },
            { id: 's4', label: 'Land/Ice', xk: 0.52, yk: 0.22 },
            { id: 's5', label: 'Temperature trend', xk: 0.82, yk: 0.42 },
          ],
          links: [
            { from: 's1', to: 's2', label: 'incoming energy' },
            { from: 's2', to: 's3', label: 'heat uptake' },
            { from: 's2', to: 's4', label: 'surface effects' },
            { from: 's3', to: 's5', label: 'stored heat' },
            { from: 's4', to: 's5', label: 'albedo change' },
          ],
        },
      },
    },
    climate_node_1: {
      topicId: 'climate_node_1',
      explanation:
        'Climate forcing starts a change, and feedback loops determine whether that change grows stronger, fades, or stabilizes.',
      keyPoints: [
        'Positive feedback amplifies change',
        'Negative feedback resists change',
        'Net effect depends on interacting loops',
      ],
      examples: ['Ice-albedo feedback amplifying warming'],
      bridgeFromPrevious: 'The system map shows where interactions happen.',
      bridgeToNext: 'Then we connect those loops to human-driven change.',
      pedagogicalDiagram: {
        variant: 'graph',
        pattern: 'GRAPH_TRAVERSAL',
        title: 'Feedback Loop Network',
        subtitle: 'How one climate change triggers another',
        durationSec: 6,
        graph: {
          nodes: [
            { id: 'a', label: 'CO2 rise', xk: 0.18, yk: 0.36 },
            { id: 'b', label: 'Warming', xk: 0.44, yk: 0.36 },
            { id: 'c', label: 'Ice loss', xk: 0.68, yk: 0.22 },
            { id: 'd', label: 'Lower albedo', xk: 0.82, yk: 0.42 },
            { id: 'e', label: 'More warming', xk: 0.66, yk: 0.7 },
          ],
          edges: [
            { from: 'a', to: 'b', label: 'radiative forcing', directed: true },
            { from: 'b', to: 'c', label: 'melting', directed: true },
            { from: 'c', to: 'd', label: 'less reflection', directed: true },
            { from: 'd', to: 'e', label: 'extra absorption', directed: true },
          ],
        },
      },
    },
    climate_node_2: {
      topicId: 'climate_node_2',
      explanation:
        'Human activity changes long-term climate by shifting greenhouse gas concentrations faster than many natural balancing processes can compensate.',
      keyPoints: [
        'Trend changes are cumulative, not isolated spikes',
        'Human forcing operates across decades and centuries',
        'Mitigation changes the slope, not just the moment',
      ],
      examples: ['Emissions pathways producing different warming trajectories'],
      bridgeFromPrevious: 'Feedback loops explain why forcing matters.',
      bridgeToNext: '',
      pedagogicalDiagram: {
        variant: 'chart',
        pattern: 'VALUE_ACCUMULATION',
        title: 'Long-Term Warming Pathways',
        subtitle: 'Different forcing levels produce different futures',
        durationSec: 6,
        chart: {
          type: 'line',
          labels: ['Now', '2030', '2050', '2075', '2100'],
          series: [
            { name: 'High emissions', values: [1.1, 1.5, 2.2, 3.0, 3.8] },
            { name: 'Mitigation path', values: [1.1, 1.4, 1.8, 2.0, 2.1] },
          ],
        },
      },
    },
  },
});

export const DEMO_SCENE_TOPICS = [
  {
    key: 'ai',
    label: 'AI for Everyone',
    request: {
      topic: 'Artificial Intelligence for Everyone',
      courseTitle: 'Most Learned Public Demo',
      lessonObjective:
        'Understand why AI literacy matters and how modern AI systems work in practice.',
      learner: baseLearner,
      animation: baseAnimation,
      presentationMode: 'scene',
      scenePolicy: baseScenePolicy,
    },
    staticSceneData: {
      lessonId: 'static_ai_for_everyone',
      title: 'AI for Everyone',
      treeSlides: aiTreeSlides,
      nodeContentById: aiNodeContent,
    },
  },
  {
    key: 'middle-east-history',
    label: 'History of the Middle East',
    request: {
      topic: 'History of the Middle East',
      courseTitle: 'Most Learned Public Demo',
      lessonObjective:
        'Build a clear historical timeline and understand major turning points shaping the modern Middle East.',
      learner: baseLearner,
      animation: baseAnimation,
      presentationMode: 'scene',
      scenePolicy: baseScenePolicy,
    },
    staticSceneData: buildMiddleEastStaticScene(),
  },
  {
    key: 'education-revolution',
    label: 'Revolution of Education Systems',
    request: {
      topic: 'Revolution of Education Systems History',
      courseTitle: 'Most Learned Public Demo',
      lessonObjective:
        'Trace how education evolved from elite instruction to mass, digital, and AI-personalized learning.',
      learner: baseLearner,
      animation: baseAnimation,
      presentationMode: 'scene',
      scenePolicy: baseScenePolicy,
    },
    staticSceneData: buildEducationStaticScene(),
  },
  {
    key: 'differential-equations',
    label: 'Differential Equations',
    request: {
      topic: 'Differential Equations',
      courseTitle: 'Most Learned Public Demo',
      lessonObjective:
        'Understand what differential equations model and how they describe physical and real-world systems.',
      learner: baseLearner,
      animation: baseAnimation,
      presentationMode: 'scene',
      scenePolicy: baseScenePolicy,
    },
    staticSceneData: buildDifferentialEquationsStaticScene(),
  },
  {
    key: 'climate-systems',
    label: 'Climate Systems',
    request: {
      topic: 'Climate Systems and Change',
      courseTitle: 'Most Learned Public Demo',
      lessonObjective:
        'Explain climate system feedback loops and how human activity changes long-term climate behavior.',
      learner: baseLearner,
      animation: baseAnimation,
      presentationMode: 'scene',
      scenePolicy: baseScenePolicy,
    },
    staticSceneData: buildClimateStaticScene(),
  },
];

export function getDemoSceneTopicByKey(key) {
  const normalized = String(key || '')
    .toLowerCase()
    .trim();
  return DEMO_SCENE_TOPICS.find((item) => item.key === normalized) || DEMO_SCENE_TOPICS[0];
}
